import { useEffect } from 'react';

import { neighbourDays } from './mappers';
import type { BioscopeImageDay, BioscopeImageMeta } from './types';

interface UseNeighbourDayPrefetchOptions {
    days: BioscopeImageDay[];
    selectedDay: string | undefined;
    enabled: boolean;
    prefetch: (day: string) => Promise<unknown>;
    readFirstImage: (day: string) => BioscopeImageMeta | undefined;
    jpegSrc: (imageId: string) => string;
}

/**
 * Prefetch the previous/next day's image list (and the first JPEG of each).
 * Full history stays on the days slider; only neighbours are warmed.
 */
export function useNeighbourDayPrefetch(options: UseNeighbourDayPrefetchOptions): void {
    const { days, selectedDay, enabled, prefetch, readFirstImage, jpegSrc } = options;

    useEffect(() => {
        if (!enabled || days.length === 0) {
            return;
        }
        let cancelled = false;
        const neighbours = neighbourDays(days, selectedDay);
        for (const day of neighbours) {
            void prefetch(day).then(() => {
                if (cancelled) {
                    return;
                }
                const first = readFirstImage(day);
                if (!first) {
                    return;
                }
                const jpeg = new Image();
                jpeg.src = jpegSrc(first.id);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [days, selectedDay, enabled, prefetch, readFirstImage, jpegSrc]);
}
